import { getAuthHeaders } from './authActions'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'
const USER_API_BASE = `${BASE_URL}/api/users/user`

export const getCurrentUser = async () => {
	const res = await fetch(`${BASE_URL}/api/users/me`, {
		method: 'GET',
		headers: {
			...getAuthHeaders(),
		},
	})

	if (!res.ok) {
		throw new Error('Failed to fetch current user')
	}

	return res.json()
}

export const updateCurrentUserProfile = async ({ name, phone }) => {
	const res = await fetch(`${BASE_URL}/api/users/me`, {
		method: 'PATCH',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ name, phone }),
	})

	if (!res.ok) {
		let message = 'Failed to update profile'
		try {
			const data = await res.json()
			if (data?.message) message = data.message
		} catch {
			// no-op; keep default message
		}
		throw new Error(message)
	}

	const data = await res.json()
	if (data?.token) {
		localStorage.setItem('token', data.token)
	}
	return data
}

export const getCurrentUserPositionsForOrg = async (orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/users/me/org/${encodeURIComponent(orgSlug)}/positions`,
		{
			method: 'GET',
			headers: {
				...getAuthHeaders(),
			},
		}
	)

	if (!res.ok) {
		let message = 'Failed to fetch experience options'
		try {
			const data = await res.json()
			if (data?.message) message = data.message
		} catch {
			// no-op; keep default message
		}
		throw new Error(message)
	}

	return res.json()
}

export const updateUserRole = async (userId, role, orgId, { phone } = {}) => {
	const res = await fetch(`${USER_API_BASE}/${userId}`, {
		method: 'PATCH',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ role, orgId, phone }),
	})

	if (!res.ok) {
		let message = 'Failed to update user'
		try {
			const data = await res.json()
			if (data?.message) message = data.message
		} catch {
			// no-op; keep default message
		}
		throw new Error(message)
	}

	return res.json()
}

export const createOrgUser = async (userData) => {
	const res = await fetch(`${USER_API_BASE}`, {
		method: 'POST',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(userData),
	})

	if (!res.ok) {
		let message = 'Failed to create user'
		let data = null
		try {
			data = await res.json()
			if (data?.message) message = data.message
		} catch {
			// no-op; keep default message
		}
		const error = new Error(message)
		if (data?.code) error.code = data.code
		if (data?.user) error.user = data.user
		throw error
	}

	return res.json()
}

export const reactivateOrgUser = async (userId, { role, orgId } = {}) => {
	const res = await fetch(`${USER_API_BASE}/${userId}/reactivate`, {
		method: 'POST',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ role, orgId }),
	})

	if (!res.ok) {
		let message = 'Failed to reactivate user'
		try {
			const data = await res.json()
			if (data?.message) message = data.message
		} catch {
			// no-op; keep default message
		}
		throw new Error(message)
	}

	return res.json()
}

export const resendUserInvite = async (userId, orgId) => {
	const res = await fetch(`${USER_API_BASE}/${userId}/resend-invite`, {
		method: 'POST',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ orgId }),
	})

	if (!res.ok) {
		let message = 'Failed to resend invite'
		try {
			const data = await res.json()
			if (data?.message) message = data.message
		} catch {
			// no-op; keep default message
		}
		throw new Error(message)
	}

	return res.json()
}

export const switchOrg = async (orgSlug) => {
	const res = await fetch(`${USER_API_BASE}/current-org`, {
		method: 'PATCH',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ orgSlug }),
	})

	if (!res.ok) {
		throw new Error('Failed to switch organization')
	}

	const data = await res.json()
	if (data?.token) {
		localStorage.setItem('token', data.token)
	}
	return data
}

export const switchExperience = async (experienceSlug) => {
	const res = await fetch(`${USER_API_BASE}/current-experience`, {
		method: 'PATCH',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ experienceSlug }),
	})

	if (!res.ok) {
		throw new Error('Failed to switch experience')
	}

	const data = await res.json()
	if (data?.token) {
		localStorage.setItem('token', data.token)
	}
	return data
}

export const deleteUserRole = async (userId, orgId) => {
	const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''
	const res = await fetch(`${USER_API_BASE}/${userId}${query}`, {
		method: 'DELETE',
		headers: {
			...getAuthHeaders(),
		},
	})

	if (!res.ok) {
		let message = 'Failed to delete user role'
		try {
			const data = await res.json()
			if (data?.message) message = data.message
		} catch {
			// no-op; keep default message
		}
		throw new Error(message)
	}

	return res.json()
}

export const getUserPositions = async (userId, orgId) => {
	const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''
	const res = await fetch(`${USER_API_BASE}/${userId}/positions${query}`, {
		headers: getAuthHeaders(),
	})
	const data = await res.json()
	if (!res.ok) throw new Error(data.message || 'Failed to load positions')
	return data
}

export const setUserDefaultPosition = async (userId, positionId, orgId) => {
	const res = await fetch(`${USER_API_BASE}/${userId}/default-position`, {
		method: 'PATCH',
		headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
		body: JSON.stringify({ positionId, orgId }),
	})
	const data = await res.json()
	if (!res.ok) throw new Error(data.message || 'Failed to set default position')
	return data
}
